import { z } from "zod";
/** Decimal strings are preserved; two-decimal currency formatting is a presentation concern. */
const exact = z
    .string()
    .regex(/^(?:0|[1-9]\d{0,30})(?:\.\d{1,80})?$/)
    .max(112);
const fraction = z.string().regex(/^(?:0(?:\.\d{1,18})?|1(?:\.0{1,18})?)$/);
const hash = z.string().regex(/^[a-f0-9]{64}$/);
/** Technical equivalence is replayed server-side, never a caller-provided approval. */
export const applianceFcpLegalRegionEquivalenceSchema = z
    .object({
    algorithm: z.literal("FCP_SP_ART52_LEGAL_REGION_V1"),
    canonicalUrl: z.literal("https://legislacao.fazenda.sp.gov.br/Paginas/art052.aspx"),
    actualRawSha256: hash,
    reviewedRawSha256: z.literal("9b66d319a0657a0fdc1f101b12ec485c1903040ac5360117f7a5939d8fdb7a47"),
    canonicalSha256: z.literal("6043ae52ed789e3d63799434d46a71c78f8f357c69cf6c818987cbf90c8e46e4"),
    receiptSha256: hash,
})
    .strict();
export const applianceFcpBotShellEquivalenceSchema = z
    .object({
    algorithm: z.literal("FCP_SP_ART52_BOT_SHELL_V2"),
    canonicalUrl: z.literal("https://legislacao.fazenda.sp.gov.br/Paginas/art052.aspx"),
    actualRawSha256: hash,
    reviewedRawSha256: z.literal("9b66d319a0657a0fdc1f101b12ec485c1903040ac5360117f7a5939d8fdb7a47"),
    canonicalSha256: z.literal("9f7a1f54659d912ccc9ec1557e51f6430e69946a5606b0bd3f82b3f031ecb94c"),
    receiptSha256: hash,
})
    .strict();
export const appliancePlanaltoLawEquivalenceSchema = z.discriminatedUnion("canonicalUrl", [
    z
        .object({
        algorithm: z.literal("PLANALTO_F5_TRAILER_V1"),
        canonicalUrl: z.literal("https://www.planalto.gov.br/ccivil_03/_ato2019-2022/2022/decreto/d11158.htm"),
        actualRawSha256: hash,
        reviewedRawSha256: z.literal("e37096cd847effde7b6ac5dce61d12cdaaa932d336ab5a288971bed70141838d"),
        canonicalSha256: z.literal("596eccbb7be2e959010f79c07cd15fa1bbfe060ed93812bff8dab1a04238cb3c"),
        receiptSha256: hash,
    })
        .strict(),
    z
        .object({
        algorithm: z.literal("PLANALTO_F5_TRAILER_V1"),
        canonicalUrl: z.literal("https://www.planalto.gov.br/ccivil_03/leis/2002/l10637.htm"),
        actualRawSha256: hash,
        reviewedRawSha256: z.literal("33a014ab48bf8167b80471e812db516f6b0a012e56ea20fb2af4795c617451c2"),
        canonicalSha256: z.literal("4c9920152f91be79059cfe5b0bfc71dcc3a0ea8a73b7b01b7bf5c4231228d6eb"),
        receiptSha256: hash,
    })
        .strict(),
    z
        .object({
        algorithm: z.literal("PLANALTO_F5_TRAILER_V1"),
        canonicalUrl: z.literal("https://www.planalto.gov.br/ccivil_03/leis/2003/l10.833.htm"),
        actualRawSha256: hash,
        reviewedRawSha256: z.literal("f89ed8a3ea6284b384e64755c8eae7881d3f4da2a862e5e7fe8e6189b7218b89"),
        canonicalSha256: z.literal("f89ed8a3ea6284b384e64755c8eae7881d3f4da2a862e5e7fe8e6189b7218b89"),
        receiptSha256: hash,
    })
        .strict(),
]);
export const applianceLegalTechnicalEquivalenceSchema = z.union([
    applianceFcpLegalRegionEquivalenceSchema,
    applianceFcpBotShellEquivalenceSchema,
    appliancePlanaltoLawEquivalenceSchema,
]);
const st = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("NOT_APPLICABLE"), fcpRate: fraction }).strict(),
    z
        .object({
        kind: z.literal("APPLICABLE"),
        originalMva: exact,
        internalRate: fraction,
        adjustment: z.enum(["INTERSTATE", "ORIGINAL"]),
        fcpRate: fraction,
    })
        .strict(),
]);
/** Server proof of a current retrieval of the unchanged reviewed legal catalog. */
export const applianceLegalCurrentnessSchema = z
    .object({
    version: z.literal("APPLIANCE_LEGAL_2026_V1"),
    calculationDate: z.string().date(),
    sources: z
        .array(z
        .object({
        evidenceId: z.string().min(1).max(200),
        canonicalUrl: z.string().url().max(2048),
        contentSha256: hash,
        fetchedAt: z.string().datetime(),
        verificationReceiptId: z.string().min(1).max(200),
        observationReceiptId: z.string().min(1).max(200).optional(),
        technicalEquivalence: applianceLegalTechnicalEquivalenceSchema.optional(),
    })
        .strict())
        .min(5)
        .max(20),
})
    .strict();
/** Quote for publication, before a buyer exists. This never asks for a future sale leg. */
export const publicationPricingInputSchema = z
    .object({
    version: z.literal("ACQUISITION_PUBLICATION_V1"),
    takeRate: z.string().regex(/^0(?:\.\d{1,18})?$/),
    acquisitionCharges: exact,
    additionalStBase: exact,
    /** Transaction declarations; absent on historical inputs, never inferred by the engine. */
    purchasePurpose: z.enum(["RESALE", "OWN_USE", "FIXED_ASSET"]).optional(),
    recipientIcmsTaxpayer: z.boolean().optional(),
    /** Supplier invoice declaration for this transaction; never a published fiscal rate. */
    supplierIpi: z
        .object({
        source: z.literal("SUPPLIER_DECLARED"),
        rate: fraction,
        amount: exact.optional(),
        /** Declared row provenance, not authentication or fiscal approval. */
        rateCell: z.string().min(1).max(200).regex(/\S/),
        amountCell: z.string().min(1).max(200).optional(),
    })
        .strict()
        .optional(),
})
    .strict();
/** Server-owned: public entry points remove this and reload qualified repository evidence. */
export const acquisitionQualificationSchema = z
    .object({
    version: z.literal("APPLIANCE_ACQUISITION_2026_V1"),
    receiptSha256: hash,
    organizationId: z.string().uuid(),
    productRef: z.string().min(1).max(200),
    calculationDate: z.string().date(),
    ncm: z.string().regex(/^\d{8}$/),
    cest: z
        .string()
        .regex(/^\d{2}\.\d{3}\.\d{2}$/)
        .nullable(),
    originUf: z.string().regex(/^[A-Z]{2}$/),
    destinationUf: z.literal("SP"),
    supplierType: z.enum(["INDUSTRY", "DISTRIBUTOR"]),
    goodsOrigin: z
        .string()
        .regex(/^[0-8]$/)
        .nullable(),
    rulesetContentSha256: hash,
    ownIcmsRate: fraction,
    ipiRate: fraction,
    pisRate: fraction,
    cofinsRate: fraction,
    acquisitionCredit: z.enum(["ELIGIBLE", "INELIGIBLE", "UNCONFIRMED"]),
    st,
    evidenceReferences: z.array(z.string().min(1).max(2048)).min(1).max(100),
    legalCurrentness: applianceLegalCurrentnessSchema.optional(),
})
    .strict();
const unit = z
    .object({
    principal: exact,
    acquisitionCharges: exact,
    ownIcms: exact,
    pisCofinsBase: exact,
    pis: exact,
    cofins: exact,
    contributionCredit: exact.nullable(),
    ipi: exact,
    originalMva: exact.nullable(),
    appliedMva: exact.nullable(),
    stBase: exact,
    icmsSt: exact,
    fcp: exact,
    acquisitionCost: exact,
    netCostAfterContributionCredit: exact.nullable(),
    takeAmount: exact,
    listingPrice: exact,
})
    .strict();
const memory = z
    .array(z
    .object({
    code: z.string().min(1).max(100),
    formula: z.string().min(1).max(500),
    inputs: z.record(z.string(), z.string().max(2048)),
    result: exact,
})
    .strict())
    .min(1)
    .max(30);
export const acquisitionPublicationResultSchema = z.discriminatedUnion("status", [
    z
        .object({
        status: z.literal("PENDING"),
        version: z.literal("ACQUISITION_PUBLICATION_V1"),
        missing: z.array(z.string().min(1).max(120)).min(1).max(30),
        /** Retained transaction comparison when supplier IPI monetary facts conflict. */
        memory: memory.optional(),
    })
        .strict(),
    z
        .object({
        status: z.literal("CALCULATED"),
        version: z.literal("ACQUISITION_PUBLICATION_V1"),
        receiptSha256: hash,
        /** Older retained results predate the explicit credit status. */
        acquisitionCredit: z.enum(["ELIGIBLE", "INELIGIBLE", "UNCONFIRMED"]).optional(),
        precision: z
            .object({
            significantDigits: z.literal(40),
            intermediateCurrencyRounding: z.literal(false),
        })
            .strict(),
        unit,
        line: unit,
        memory,
        evidenceReferences: acquisitionQualificationSchema.shape.evidenceReferences,
    })
        .strict(),
]);
//# sourceMappingURL=acquisition-publication.js.map